import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { normalizeAuthUser } from "../utils/userProfile";
import {
    clearAuthSession,
    getStoredToken,
    getStoredUser,
    storeAuthSession,
    updateStoredUser,
} from "../utils/authStorage";
import { setHydrationComplete } from "../sdkAdapter";

interface User {
    id?: number;
    username?: string;
    name: string;
    email?: string;
    role?: string;
    avatar?: any;
    image?: any;
    image_id?: number | string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (token: string, userData: any) => void;
    updateUser: (userData: any) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const token = getStoredToken();
        const userData = getStoredUser();

        console.log('[AuthContext] Starting hydration, token exists:', !!token, 'userData exists:', !!userData);

        const hydrateUser = async () => {
            if (token) {
                if (userData) {
                    try {
                        const parsedUser = normalizeAuthUser(JSON.parse(userData));
                        console.log('[AuthContext] Parsed user data:', parsedUser);

                        if (isMounted) {
                            setUser(parsedUser);
                            setIsAuthenticated(true);
                            console.log('[AuthContext] User authenticated, isAuthenticated set to true');
                        }

                        // Only refresh profile if we have valid user data
                        // Don't make this call during initial hydration to avoid 401 issues
                        // Profile refresh can be done separately or on user action
                    } catch (e) {
                        console.error("[AuthContext] Failed to parse user data", e);
                        if (isMounted) {
                            clearAuthSession();
                            setUser(null);
                            setIsAuthenticated(false);
                        }
                    }
                } else if (isMounted) {
                    console.log('[AuthContext] No user data, setting default user');
                    setUser({ name: 'User' });
                    setIsAuthenticated(true);
                }
            } else {
                console.log('[AuthContext] No token found, user not authenticated');
            }

            if (isMounted) {
                setIsLoading(false);
                console.log('[AuthContext] Hydration complete, isLoading set to false');
                // Mark hydration as complete so 401 interceptor can work properly
                setHydrationComplete();
            }
        };

        void hydrateUser();

        return () => {
            isMounted = false;
        };
    }, []);

    const login = (token: string, userData: any) => {
        const safeUser: User = normalizeAuthUser(userData);

        storeAuthSession(token, JSON.stringify(safeUser));
        setUser(safeUser);
        setIsAuthenticated(true);
    };

    const updateUser = (userData: any) => {
        const nextUser = normalizeAuthUser({
            ...(user || {}),
            ...(userData || {}),
        });
        updateStoredUser(JSON.stringify(nextUser));

        setUser(nextUser);
    };

    const logout = () => {
        clearAuthSession();
        setUser(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, updateUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
